import styles from "./Modals.module.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { FormTextArea } from "@whatslly/ui-components/v2/FormElements/FormTextArea";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { useTranslation } from "react-i18next";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import classNames from "classnames";
import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { observer } from "mobx-react-lite";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { DESTINATIONS_FILENAME_TEMPLATES } from "@whatslly/commons/destinationsFileNameTemplates";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";

const FILENAME_TEMPLATE = '{{orgId}}/{{sourcePhone}}/{{date timestamp "YYYY/MM/DD"}}.';
const FILENAME_TEMPLATE_OPTIONS = DESTINATIONS_FILENAME_TEMPLATES.map((template) => ({ label: template, value: template }));

const BASE_DATA: Destination = {
  type: "googleCloudStorage" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
  settings: {
    format: "csv",
    projectId: "",
    client_email: "",
    private_key: "",
    bucket: "",
    filenameTemplate: `${FILENAME_TEMPLATE}csv`,
    mediaFilenameTemplate: "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}",
  },
};
type FormDataType = FormDestinationDataType<"googleCloudStorage">;

export const GoogleCloudStorageModalForm: React.FC<DestinationFormProps<"googleCloudStorage">> = observer(({ onSubmitForm, settings }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const [formatValue, setFormatValue] = React.useState<"json" | "csv">(settings?.format ?? "csv");
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const isSuperAdmin = useMemo(() => userStore.user?.isSuperAdmin || userStore.user?.isAdmin, []);
  const [filenameTemplateSelect, setFilenameTemplateSelect] = useState<{ label: string; value: string } | undefined>();

  const validate = async (values: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.format) errors.format = t("field_is_required");
    if (!values.projectId) errors.projectId = t("field_is_required");
    if (!values.client_email) errors.client_email = t("field_is_required");
    if (!values.private_key) errors.private_key = t("field_is_required");
    if (!values.bucket) errors.bucket = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);

    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    return { ...BASE_DATA, settings: { ...data, format: formatValue } };
  };

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  const onChangeFormat = (value: "json" | "csv") => {
    setFormatValue(value);
    const currentFileNameTemplateValue = formikContext.current?.values.filenameTemplate || `${FILENAME_TEMPLATE}.${value}`;
    const [currentValue] = currentFileNameTemplateValue.split(".");
    const newFileNameTemplateValue = FILENAME_TEMPLATE_OPTIONS.find((opt) => {
      const [optValue, optFormat] = opt.value.split(".");
      return optValue === currentValue && optFormat === value;
    }) || {
      label: `${FILENAME_TEMPLATE}.${value}`,
      value: `${FILENAME_TEMPLATE}.${value}`,
    };
    setFilenameTemplateSelect(newFileNameTemplateValue);
    formikContext.current?.setFieldValue("filenameTemplate", newFileNameTemplateValue?.value || `${FILENAME_TEMPLATE}.${value}`);
  };

  const onFileNameTemplateChange = (value: string) => {
    const newMediaFilenameTemplatePrefix = value?.startsWith("{{sourcePhone}}") ? "" : "{{orgId}}/";
    formikContext.current?.setFieldValue("mediaFilenameTemplate", `${newMediaFilenameTemplatePrefix}{{sourcePhone}}/media/{{_id}}.{{fileType}}`);
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  return (
    <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.format")} />
        <FormSelect<{ value: "json" | "csv" }>
          name={"format"}
          paddingInline="none"
          options={[
            { label: "JSON", value: "json" },
            { label: "CSV", value: "csv" },
          ]}
          onChange={(selectedOption: { value: "json" | "csv" }) => onChangeFormat(selectedOption?.value)}
          defaultValue={settings?.format ? { label: settings.format.toUpperCase(), value: settings.format } : { label: "CSV", value: "csv" }}
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.bucket")} required />
        <FormTextInput name={"bucket"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.project_id")} required />
        <FormTextInput name={"projectId"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.client_email")} required />
        <FormTextInput name={"client_email"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.private_key")} required />
        <FormTextArea name={"private_key"} paddingInline="none" labelText="" />
      </div>
      <div className={classNames(styles.field, { [styles.hidden]: !isSuperAdmin })}>
        <Label className={styles.filedLabel} text={t("destinations_page.file_name_template")} required />
        <Select
          options={FILENAME_TEMPLATE_OPTIONS.filter((opt) => {
            return opt.value.includes(formatValue ?? "csv");
          })}
          value={filenameTemplateSelect}
          paddingInline="none"
          onChange={(selectedOption: (typeof FILENAME_TEMPLATE_OPTIONS)[number]) => {
            formikContext.current?.setFieldValue("filenameTemplate", selectedOption?.value);
            onFileNameTemplateChange(selectedOption?.value);
          }}
          defaultValue={
            FILENAME_TEMPLATE_OPTIONS.find((opt) =>
              settings?.filenameTemplate ? settings.filenameTemplate === opt.value : opt.value.includes(formatValue ?? "csv")
            ) ?? {
              label: `${FILENAME_TEMPLATE}csv`,
              value: `${FILENAME_TEMPLATE}csv`,
            }
          }
        />
        <div className={classNames(styles.field, styles.hidden)}>
          <FormTextInput name={"filenameTemplate"} paddingInline="none" />
        </div>
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.store_media")} />
        <FormCheckBox name={"storeMedia"} paddingInline="none" />
      </div>
      <div className={classNames(styles.field, { [styles.hidden]: !isSuperAdmin })}>
        <Label className={styles.filedLabel} text={t("destinations_page.media_file_name_template")} />
        <FormTextInput name={"mediaFilenameTemplate"} paddingInline="none" disabled />
      </div>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
});
